// ============================================================================
// CE QU'UN PRIX DE VENTE ET UN PRIX D'ACHAT DONNENT — HT, TVA, marge, coefficient.
//
// ⚠️ CE MODULE NE CALCULE RIEN LUI-MÊME. Il ne fait que deux choses : NOMMER les quatre indicateurs de
// l'écran, et décider quand il n'y en a PAS. Toute l'arithmétique est déléguée aux cœurs purs —
// `core-vat` pour la ventilation (règle figée §30.3), `core-money` pour les ratios, les pourcentages et
// les changements d'échelle. Refaire ici une division « qui revient au même » créerait une seconde
// arithmétique de l'argent à côté de celle que la caisse applique, avec ses propres arrondis.
//
// ⚠️ POURQUOI IL EXISTE MALGRÉ TOUT. La fiche article (onglet Prix) et l'assistant Gamme (étape 4)
// montrent les MÊMES quatre indicateurs sur les mêmes deux montants. Recalculés de chaque côté, ils
// finiraient par diverger — un même article afficherait deux coefficients selon l'écran qui le montre.
// C'est la Forme A du catalogue, et elle ne se voit jamais tout de suite.
//
// ⚠️ UN SEUL COEFFICIENT, ET IL VA DU PRIX D'ACHAT HT AU PRIX DE VENTE TTC : coef = PV TTC ÷ PA HT, et
// inversement PV TTC = PA HT × coef, PA HT = PV TTC ÷ coef (décision utilisateur ; la maquette l'écrit
// « la définition retenue partout »). Le COEFFICIENT FOURNISSEUR d'un corner se lit de la même façon — celui de
// l'ancien logiciel aussi (936 € TTC ÷ 689 € d'achat = 1,358491). Un second coefficient, lu sur le HT, donnerait
// sous le même nom deux nombres qui diffèrent de la TVA, et un prix d'achat de corner faux d'autant.
//
// ⚠️ DEUX ÉCHELLES COHABITENT : le prix de vente est en CENTIMES (échelle 2), le prix d'achat en
// échelle FINE (4, pour les achats à quatre décimales). Les convertir passe par `rescale`, jamais par
// un ×100 posé à la main — c'est là que se logent les résultats cent fois faux et plausibles.
//
// ⚠️ `null` PARTOUT OÙ RIEN NE FONDE LE CALCUL, jamais zéro. Les cœurs, eux, rendent 0 sur un
// dénominateur nul : c'est la bonne garde EN CAISSE, où rien ne doit planter pendant un encaissement.
// À l'écran c'est l'inverse — « pas de marge connue » et « marge nulle » n'appellent pas le même
// geste, et zéro se lirait comme une vente à prix coûtant. La décision d'absence vit donc ICI.
// ============================================================================
import { mulDivRound, pctBp, ratioScaled, rescale } from '@orgazone/core-money';
import { htFromTtc } from '@orgazone/core-vat';

/** Les échelles nommées, pour que `rescale(x, 2, 4)` se lise sans compter les zéros. */
const CENTIME = 2;
const FINE = 4;

/**
 * Un montant d'échelle FINE ramené aux CENTIMES, pour l'affichage.
 *
 * ⚠️ IL EXISTE POUR QU'AUCUN ÉCRAN N'ÉCRIVE `Math.round(x / 100)` (correctif d'audit 2026-08-25) —
 * geste que l'en-tête de ce module interdit explicitement, et que l'onglet Prix faisait quand même.
 * L'arrondi appartient au cœur : recopié à la main, il tombe un jour du mauvais côté du demi-centime,
 * et le prix d'achat théorique affiché cesse de correspondre à celui que la caisse retiendra.
 */
export const fineEnCentimes = (fine: number): number => rescale(fine, FINE, CENTIME);

/**
 * Un coefficient CONTRACTUEL (points de base) dans l'unité des coefficients DÉRIVÉS (centièmes) —
 * pour comparer les deux ou nourrir `ttcFromCoeff`.
 * ⚠️ ICI et pas dans chaque écran : « ÷100 » posé à la main dans deux écrans, c'est la naissance
 * d'une seconde conversion — et l'arrondi vit dans `mulDivRound`, jamais dans un flottant.
 */
export const coeffCentsFromBp = (bp: number | null): number | null =>
  (bp == null || bp <= 0 ? null : mulDivRound(bp, 1, 100));

/**
 * LE COEFFICIENT d'un couple de prix — PV TTC ÷ PA HT, en centièmes ; `null` sans prix de vente ou sans achat positif.
 * ⚠️ LA TVA N'Y ENTRE PAS : elle est déjà dans le prix de vente. C'est la seule écriture du coefficient dérivé — la
 *    fiche, l'assistant Gamme et la création d'article la lisent ici.
 */
export function coeffFromPrices(ttcCents: number | null, purchaseFine: number | null): number | null {
  if (ttcCents === null || purchaseFine === null || purchaseFine <= 0) return null;
  return ratioScaled(rescale(ttcCents, CENTIME, FINE), purchaseFine, 2);
}

export interface IndicateursPrix {
  /** Prix de vente hors taxe, en centimes. */
  htCents: number | null;
  /** Montant de TVA, en centimes. */
  tvaCents: number | null;
  /** Marge sur le HT, en points de base (4025 = 40,25 %). */
  margeBp: number | null;
  /** Marge en centimes — 40 % sur un tee-shirt et sur un manteau ne mettent pas la même somme en caisse. */
  margeCents: number | null;
  /** Coefficient en centièmes (250 = 2,50). */
  coeffCents: number | null;
}

/**
 * Les quatre indicateurs d'un couple (prix de vente TTC, prix d'achat).
 *
 * @param ttcCents  prix de vente TTC en centimes, `null` si non saisi
 * @param achatFine prix d'achat en échelle fine, `null` si non connu
 * @param tvaBp     taux de TVA en points de base (2000 = 20 %)
 * @param coefContractuelBp coefficient CONTRACTUEL en points de base, pour le régime corner.
 *   ⚠️ En corner le coefficient n'est PAS dérivé : il EST la donnée d'entrée, et c'est lui qui produit
 *   le prix d'achat. Le recalculer depuis ce prix serait circulaire, et masquerait son absence
 *   derrière un nombre parfaitement crédible.
 */
export function indicateursPrix(
  ttcCents: number | null,
  purchaseFine: number | null,
  tvaBp: number,
  contractualCoeffBp: number | null = null,
): IndicateursPrix {
  const htCents = ttcCents === null ? null : htFromTtc(ttcCents, tvaBp);
  /**
   * ⚠️ PAR SOUSTRACTION, ET NON PAR `vatFromHt`. Redescendre du HT rouvrirait un second arrondi, et
   * la somme affichée ne retomberait pas toujours sur le TTC que l'utilisateur vient de taper — un
   * centime d'écart entre trois nombres posés côte à côte se lit comme une erreur de la machine.
   */
  const tvaCents = ttcCents === null || htCents === null ? null : ttcCents - htCents;

  const htFine = htCents === null ? null : rescale(htCents, CENTIME, FINE);

  const coeffCents = contractualCoeffBp !== null
    ? mulDivRound(contractualCoeffBp, 1, 100)
    : coeffFromPrices(ttcCents, purchaseFine);

  const margeBp = htFine !== null && purchaseFine !== null && htFine > 0
    ? pctBp(htFine - purchaseFine, htFine)
    : null;
  const margeCents = htCents !== null && purchaseFine !== null
    ? htCents - rescale(purchaseFine, FINE, CENTIME)
    : null;

  return { htCents, tvaCents, margeBp, margeCents, coeffCents };
}

/**
 * LE PRIX D'ACHAT THÉORIQUE d'un corner — projection, jamais un coût constaté.
 *
 * ⚠️ C'est une PROJECTION que chaque vente dément : en corner, le prix d'achat réel se fige à
 * l'encaissement, depuis le prix réellement payé. Affiché sans être nommé « théorique », il ferait
 * croire à un coût connu d'avance.
 * ⚠️ Rendu en échelle FINE, comme un prix d'achat saisi — pour que les deux entrent dans les mêmes
 * calculs sans conversion oubliée en chemin.
 * ⚠️ PV TTC ÷ coef, comme tout coefficient : la TVA n'y entre pas, elle est déjà dans le prix de vente.
 */
export function achatTheoriqueCorner(
  ttcCents: number | null,
  contractualCoeffBp: number | null,
): number | null {
  if (ttcCents === null || contractualCoeffBp === null || contractualCoeffBp <= 0) return null;
  return rescale(mulDivRound(ttcCents, 10_000, contractualCoeffBp), CENTIME, FINE);
}

/**
 * LE PRIX DE VENTE que produit un coefficient appliqué à un prix d'achat — l'outil de saisie de
 * l'écran (« saisir un coef recalcule le PV depuis le PA »).
 *
 * ⚠️ LE COEFFICIENT MÈNE DIRECTEMENT AU TTC (PA HT × coef) : passer par le HT puis ajouter la TVA appliquerait
 * un coefficient de plus, celui de la TVA, et le prix affiché ne redonnerait pas le coef saisi.
 */
export function ttcFromCoeff(
  purchaseFine: number | null,
  coeffCents: number | null,
): number | null {
  if (purchaseFine === null || coeffCents === null || coeffCents <= 0) return null;
  return mulDivRound(rescale(purchaseFine, FINE, CENTIME), coeffCents, 100);
}

/**
 * LE PRIX DE VENTE qu'un coefficient de CONTRAT (points de base, 13 585 = ×1,3585) donne à un prix d'achat.
 *
 * ⚠️ PAS PAR `ttcFromCoeff` : un coefficient de contrat porte quatre décimales, et l'arrondir d'abord au centième
 *    (1,3585 → 1,36) déplace le prix — 689 € d'achat donnent 937,04 € au lieu de 936,01 €. Un seul arrondi, à la fin.
 */
export function ttcFromCoeffBp(purchaseFine: number | null, coeffBp: number | null): number | null {
  if (purchaseFine === null || coeffBp === null || coeffBp <= 0) return null;
  return mulDivRound(purchaseFine, coeffBp, 1_000_000);
}

/**
 * LE PRIX D'ACHAT qu'un coefficient donne depuis un prix de vente — l'inverse de `ttcFromCoeff`, pour la saisie
 * (un coefficient tapé sans prix d'achat le déduit du prix de vente, comme la maquette).
 *
 * ⚠️ MÊME RÈGLE : PA HT = PV TTC ÷ coef. Rendu en échelle FINE, comme un prix d'achat saisi.
 */
export function purchaseFromCoeff(
  ttcCents: number | null,
  coeffCents: number | null,
): number | null {
  if (ttcCents === null || coeffCents === null || coeffCents <= 0) return null;
  return rescale(mulDivRound(ttcCents, 100, coeffCents), CENTIME, FINE);
}
